import React, { useContext, useState } from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { AngularContext } from 'react-app';

import { RootState } from 'redux/schemas';
import { getCurrentCourse } from 'redux/selectors/course';
import { useCourseHomeUtils } from 'shared/hooks/use-course-home-utils';
import NvIcon from 'shared/components/nv-icon';
import NvPopover from 'shared/components/nv-popover';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import ClickableContainer from 'components/clickable-container';
import URLS_TO_HIDE_ASK_NOVO_AI from 'institutions/components/ask-novoai/helpers';

import { threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { HelpWidgetPopover } from './help-widget-popover';

const styles = css`
  .page-link {
    cursor: pointer;
    display: inline-flex;
    align-items: center;

    &.text-white {
      color: #FFF;
    }
  }

  .icon-wrapper {
    display: inline-flex;
    align-items: center;
  }
  .bs4-modal-content {
    height: none !important;
  }
`;

interface HelpWidgetProps {
  isMobile?: boolean;
}

export const HelpWidget: React.FC<HelpWidgetProps> = ({ isMobile = false }) => {
  const { injectServices } = useContext(AngularContext);
  const [$location, InstitutionsManager] = injectServices([
    '$location',
    'InstitutionsManager',
  ]);

  const [showPopover, setShowPopover] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Redux selectors
  const helpOptions = useSelector((state: RootState) => state.app?.askNovoAi?.helpOptions);
  const currentCourse = useSelector(getCurrentCourse);
  const { isWhiteIcon, isEditOrReorderMode } = useCourseHomeUtils();

  // Check if help widget should be visible
  const showAiAssistant = InstitutionsManager?.institution?.hasAiLearningAssistant && (helpOptions?.hasAiLearningAssistantEnabled || helpOptions?.hasAiEmbeddingInProgress)
    && !currentCourse?.inArchiveMode;
  const hasHelpResources = helpOptions?.hasHelpResourcesEnabled;
  const shouldShowHelpWidget = hasHelpResources || showAiAssistant;

  // Check if current URL is in the list of URLs to hide AskNovoAI
  const currentPath = $location.path();
  const shouldDisableAskNovoAI = URLS_TO_HIDE_ASK_NOVO_AI.some(url => currentPath.includes(url));

  if (!shouldShowHelpWidget) {
    return null;
  }

  // Mobile render - menu item that opens modal
  if (isMobile) {
    return (
      <React.Fragment>
        <li className='visible-xs'>
          <a
            onClick={() => setShowModal(true)}
            role='button'
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowModal(true);
              }
            }}
          >
            {t.COURSE_HOME.HEADER.HELP()}
          </a>
        </li>

        <div css={styles}>

          {/* Modal for mobile */}
          <NvModal
            show={showModal}
            type={ModalType.DYNAMIC}
            width={500}
            fullHeight={false}
            header={
            showAiAssistant && !helpOptions?.courseHelp
              ? t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.LEARNING_ASSISTANT()
              : helpOptions?.courseHelp?.title || t.COURSE_HOME.HEADER.HELP()
          }
            body={(
              <HelpWidgetPopover
                showAiAssistant={showAiAssistant}
                shouldDisableAskNovoAI={shouldDisableAskNovoAI}
                helpOptions={helpOptions}
                onClose={() => setShowModal(false)}
              />
          )}
            onClose={() => setShowModal(false)}
          />
        </div>
      </React.Fragment>
    );
  }

  // Desktop render - icon with popover
  const popoverContent = (
    <HelpWidgetPopover
      showAiAssistant={showAiAssistant}
      shouldDisableAskNovoAI={shouldDisableAskNovoAI}
      helpOptions={helpOptions}
      onClose={() => setShowPopover(false)}
    />
  );

  const popoverStyles = css`
    .bs4-popover-body {
      padding: ${threeQuartersSpacing}px;
      height: auto;
      width: auto;
    }
  `;

  return (
    <div css={styles}>
      <NvPopover
        show={showPopover && !showModal}
        content={popoverContent}
        placement='bottom-end'
        rootClose
        onHide={() => setShowPopover(false)}
        overlayStyles={popoverStyles}
        className='help-menu'
      >
        <ClickableContainer
          className={`page-link help-link hidden-xs ${isWhiteIcon ? 'text-white' : ''}`}
          onClick={() => setShowPopover(!showPopover)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setShowPopover(false); // Close popover if open
              setShowModal(true);
            }
          }}
          role='button'
          aria-label={t.COURSE_HOME.HEADER.HELP()}
          tabIndex={isEditOrReorderMode ? -1 : 0}
          aria-haspopup='true'
          aria-expanded={showPopover || showModal}
        >
          <div className='icon-wrapper'>
            <NvIcon icon='help' size='small' />
          </div>
        </ClickableContainer>
      </NvPopover>

      {/* Modal for keyboard navigation */}
      <NvModal
        show={showModal}
        type={ModalType.DYNAMIC}
        width={500}
        fullHeight={false}
        header={
          showAiAssistant && !helpOptions?.courseHelp
            ? t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.LEARNING_ASSISTANT()
            : helpOptions?.courseHelp?.title || t.COURSE_HOME.HEADER.HELP()
        }
        body={(
          <HelpWidgetPopover
            showAiAssistant={showAiAssistant}
            shouldDisableAskNovoAI={shouldDisableAskNovoAI}
            helpOptions={helpOptions}
            onClose={() => setShowModal(false)}
          />
        )}
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};

export default HelpWidget;
